import { useSuspenseQuery } from "@tanstack/react-query";
import {
  ControlCodes,
  getControlName,
  queries,
  useAuthConfig,
} from "@theprelude/core";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@theprelude/ds";
import { useState } from "react";
import { z } from "zod";
import { useOverlay } from "@/hooks/use-overlay";
import type { SupportedMicrosoftControl } from "@/lib/azure";
import { ConnectWithEntraIdButton } from "./connect-entra-id-button";
import { MicrosoftConnectionStatus } from "./microsoft-connection-status";

export const microsoftAutomatedSetupSchema = z.tuple([
  z.literal("microsoft-automated-setup"),
]);

export type MicrosoftAutomatedSetupSchema = z.infer<
  typeof microsoftAutomatedSetupSchema
>;

interface ControlConfigurationItem {
  controlCode: SupportedMicrosoftControl;
  baseUrl: string;
}

const CONTROL_CONFIG: ControlConfigurationItem[] = [
  {
    controlCode: ControlCodes.DEFENDER,
    baseUrl: "https://api.securitycenter.microsoft.com/",
  },
  {
    controlCode: ControlCodes.INTUNE,
    baseUrl: "https://graph.microsoft.com/",
  },
  {
    controlCode: ControlCodes.M365,
    baseUrl: "https://api.securitycenter.microsoft.com/",
  },
  {
    controlCode: ControlCodes.ENTRA,
    baseUrl: "https://api.securitycenter.microsoft.com/",
  },
];

type ConnectionState = {
  appId: string;
  isConnected: boolean;
  requiredAdminConsent: boolean;
  requiredGlobalReaderRoleAssignment: boolean;
  connectedControls: Array<{
    controlCode: SupportedMicrosoftControl;
    instanceName: string;
  }>;
};

export function MicrosoftAutomatedSetupOverlay() {
  const overlay = useOverlay();
  const authConfig = useAuthConfig();
  const [connectionState, setConnectionState] = useState<ConnectionState>({
    appId: "",
    isConnected: false,
    requiredAdminConsent: false,
    requiredGlobalReaderRoleAssignment: false,
    connectedControls: [],
  });

  const { data: connectedControls } = useSuspenseQuery({
    ...queries.iam.getAccount(authConfig),
    select: (data) => data.controls.map((control) => control.id),
  });

  const controlsToConnect = CONTROL_CONFIG.filter(
    (control) => !connectedControls.includes(control.controlCode),
  ).map((control) => ({
    ...control,
    instanceId: "",
    instanceName: "",
  }));

  return (
    <Dialog open onOpenChange={() => overlay.close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Microsoft Automated Setup</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-4">
            {connectionState.isConnected ? (
              <div className="flex flex-col gap-6">
                <MicrosoftConnectionStatus
                  appId={connectionState.appId}
                  controls={connectionState.connectedControls}
                  requiredAdminConsent={connectionState.requiredAdminConsent}
                  requiredGlobalReaderRoleAssignment={
                    connectionState.requiredGlobalReaderRoleAssignment
                  }
                />
                <Button intent="secondary" onClick={() => overlay.close()}>
                  Close
                </Button>
              </div>
            ) : (
              <>
                <Header initialControls={controlsToConnect} />
                <ConnectWithEntraIdButton
                  controls={controlsToConnect}
                  onSuccess={({
                    requiredAdminConsent,
                    requiredGlobalReaderRoleAssignment,
                    appId,
                  }) => {
                    setConnectionState({
                      appId,
                      isConnected: true,
                      requiredAdminConsent,
                      requiredGlobalReaderRoleAssignment,
                      connectedControls: controlsToConnect,
                    });
                  }}
                />
              </>
            )}
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

const Header = ({
  initialControls,
}: {
  initialControls: { controlCode: SupportedMicrosoftControl }[];
}) => {
  const [controls] =
    useState<
      {
        controlCode: SupportedMicrosoftControl;
      }[]
    >(initialControls);

  return (
    <p className="text-secondary-foreground text-sm">
      Connect your Microsoft account to set up{" "}
      {controls
        .map((control) => getControlName(control.controlCode))
        .join(", ")}
      .
    </p>
  );
};
